import {
  eventGroupOfEvent,
  TASK_EVENT_GROUP_LABELS,
  TASK_EVENT_GROUPS,
  TASK_EVENT_TYPE_LABELS,
  TASK_STATUS_LABELS,
  tagKurz,
  type TaskEventGroup,
  type TaskEventType,
  type TaskSource,
  type TaskStatus,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/cn'
import { useMerker } from '../lib/merker'
import { tagesEreignisseQuery } from '../lib/queries'
import { AufgabenVerweis, useAufgabeOeffnen, useAufgabeOffen } from './AufgabeKontext'
import { SourceBadge } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * Tages-Popup – der Teil, der das Daily wirklich trägt (Konzept Kap. 4.6, AP 2.4).
 *
 * Jede Zahl und jeder Balken im Leitstand ist klickbar und landet hier: alle
 * Ereignisse einer Person an einem Tag, mit Uhrzeit, Quelle und Aufgabenbezug.
 * Optik aus dem Mockup (`#dayov` / `.dmodal` / `.ev`).
 *
 * ## Drei Dinge, die hier bewusst so sind
 *
 * **Der Filter merkt sich die Wahl** (Kap. 4.6, Abnahmekriterium AP 2.4) – über
 * `useMerker` im `localStorage`, nicht serverseitig: eine Ansichtsvorliebe je
 * Gerät.
 *
 * **Der Chip „Erledigt" greift auf den Statuswechsel**, nicht auf „geschlossen".
 * Bei 79 % der GitLab-Issues wird das Label gesetzt und das Issue nie
 * geschlossen (F-02); die Zuordnung macht `eventGroupOfEvent`.
 *
 * **Zählung im Kopf ist die gefilterte, nicht die gesamte.** „6 von 31
 * Ereignissen" statt einer Zahl, die nach dem Filtern nicht mehr zur Liste passt.
 *
 * **Im Teamblick lässt sich nach Person gruppieren** (Nils, 20.08.2026) – dafür ist
 * das Popup im Daily da: einmal reihum, was bei jedem gestern lief. Die Wahl wird
 * gemerkt wie der Filter; bei einer einzelnen Person entfällt der Schalter, dort
 * gäbe es nur eine Gruppe.
 */
export type TagesAuswahl = {
  /** `null` = ganzes Team. */
  personId: string | null
  name: string
  tag: string
}

export function TagesPopup({
  auswahl,
  schliessen,
}: {
  auswahl: TagesAuswahl
  schliessen: () => void
}) {
  const [gruppe, setGruppe] = useMerker<TaskEventGroup | 'alle'>('popup-filter', 'alle', [
    'alle',
    ...TASK_EVENT_GROUPS,
  ])
  const [ordnung, setOrdnung] = useMerker<'zeit' | 'person'>('popup-ordnung', 'zeit', [
    'zeit',
    'person',
  ])
  const detailOeffnen = useAufgabeOeffnen()
  const detailOffen = useAufgabeOffen()
  const [markiert, setMarkiert] = useState(0)
  const listeRef = useRef<HTMLDivElement>(null)

  const { data: ereignisse = [], isLoading } = useQuery(
    tagesEreignisseQuery(auswahl.personId, auswahl.tag),
  )

  const gefiltert =
    gruppe === 'alle'
      ? ereignisse
      : ereignisse.filter((e) => eventGroupOfEvent(e as EreignisFuerGruppe) === gruppe)

  /**
   * Der Schalter erscheint nur im Teamblick – bei einer einzelnen Person wäre die
   * Gruppierung eine Überschrift über allem.
   */
  const gruppierbar = auswahl.personId === null
  const bloecke: { person: string | null; ereignisse: typeof gefiltert }[] =
    gruppierbar && ordnung === 'person'
      ? nachPersonBuendeln(gefiltert)
      : [{ person: null, ereignisse: gefiltert }]

  /**
   * Die Tastatur läuft über die **angezeigte** Reihenfolge, nicht über `gefiltert` –
   * sonst springt die Markierung im gruppierten Blick quer durch die Liste.
   */
  const reihenfolge = bloecke.flatMap((b) => b.ereignisse)

  /** Wo jeder Block in `reihenfolge` beginnt – damit jede Zeile ihren flachen Index kennt. */
  const versatz: number[] = []
  for (let i = 0, summe = 0; i < bloecke.length; i++) {
    versatz.push(summe)
    summe += bloecke[i]?.ereignisse.length ?? 0
  }

  // Beim Filter- oder Ordnungswechsel darf die Markierung nicht ins Leere zeigen.
  useEffect(() => setMarkiert(0), [gruppe, ordnung, auswahl.personId, auswahl.tag])

  /**
   * Esc schließt, Pfeiltasten laufen durch die Ereignisse (Leitplanke 3).
   *
   * Esc schließt erst die Lesefassung, wenn eine offen ist – ein Esc, das zwei
   * Ebenen auf einmal zuklappt, verliert den Kontext, in dem man gerade war. Das
   * Schließen selbst macht seit dem 03.08.2026 der `AufgabeProvider`; hier wird nur
   * **nicht mitgeschlossen**.
   */
  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (!detailOffen) schliessen()
        return
      }
      if (detailOffen) return
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        setMarkiert((i) => {
          const naechste = e.key === 'ArrowDown' ? i + 1 : i - 1
          return Math.max(0, Math.min(reihenfolge.length - 1, naechste))
        })
      }
      if (e.key === 'Enter' && reihenfolge[markiert]) {
        e.preventDefault()
        detailOeffnen(reihenfolge[markiert].taskId)
      }
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [detailOffen, detailOeffnen, reihenfolge, markiert, schliessen])

  // Die markierte Zeile in den Blick holen, wenn per Tastatur gelaufen wird.
  useEffect(() => {
    listeRef.current?.querySelector('[data-markiert="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [markiert])

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-40 flex items-start justify-center bg-[rgb(23_22_20/0.32)] p-3 pt-[4vh] sm:p-6 sm:pt-[8vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label={`Ereignisse: ${auswahl.name}, ${tagKurz(auswahl.tag)}`}
    >
      <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(80vh,100%)] w-full min-w-0 max-w-[720px] flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex items-center gap-2 border-b border-line px-[18px] py-3.5">
          <h3 className="text-sm font-bold">
            {auswahl.name} · {tagKurz(auswahl.tag)}
          </h3>
          <span className="text-xs text-muted">
            {isLoading
              ? 'lädt …'
              : gruppe === 'alle'
                ? `${ereignisse.length} ${ereignisse.length === 1 ? 'Ereignis' : 'Ereignisse'}`
                : `${gefiltert.length} von ${ereignisse.length} Ereignissen`}
          </span>
          <button
            type="button"
            onClick={schliessen}
            className="ml-auto cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5" ref={listeRef}>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {(['alle', ...TASK_EVENT_GROUPS] as const).map((k) => {
              const menge =
                k === 'alle'
                  ? ereignisse.length
                  : ereignisse.filter((e) => eventGroupOfEvent(e as EreignisFuerGruppe) === k)
                      .length
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setGruppe(k)}
                  disabled={menge === 0 && k !== 'alle'}
                  className={cn(
                    'cursor-pointer rounded-lg px-[11px] py-[5px] text-xs font-semibold',
                    gruppe === k
                      ? 'bg-ink text-white'
                      : menge === 0
                        ? 'cursor-default border border-line text-muted'
                        : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]',
                  )}
                  aria-pressed={gruppe === k}
                >
                  {k === 'alle' ? 'Alle' : TASK_EVENT_GROUP_LABELS[k]}
                  {k !== 'alle' && <span className="tabular ml-1.5 opacity-60">{menge}</span>}
                </button>
              )
            })}

            {gruppierbar && (
              <button
                type="button"
                onClick={() => setOrdnung(ordnung === 'person' ? 'zeit' : 'person')}
                className={cn(
                  'ml-auto cursor-pointer rounded-lg px-[11px] py-[5px] text-xs font-semibold',
                  ordnung === 'person'
                    ? 'border border-accent-deep bg-accent-soft text-accent-deep'
                    : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]',
                )}
                aria-pressed={ordnung === 'person'}
                title="Ereignisse nach Person bündeln – für das Daily"
              >
                Nach Person
              </button>
            )}
          </div>

          {isLoading && <div className="py-2 text-[13px] text-ink2">Lädt …</div>}

          {!isLoading && gefiltert.length === 0 && (
            <div className="py-2 text-[13px] text-ink2">
              {ereignisse.length === 0
                ? 'An diesem Tag ist nichts verzeichnet.'
                : 'Kein Ereignis in dieser Gruppe – die anderen Chips zeigen, wo etwas liegt.'}
            </div>
          )}

          {bloecke.map((block, bi) => (
            <div key={block.person ?? '·zeit'} className="mb-1 last:mb-0">
              {block.person !== null && (
                <div className="sticky top-0 z-10 flex items-baseline gap-2 bg-surface pt-2.5 pb-1">
                  <h4 className="text-[12.5px] font-bold">{block.person}</h4>
                  <span className="tabular text-[11px] text-muted">
                    {block.ereignisse.length}{' '}
                    {block.ereignisse.length === 1 ? 'Ereignis' : 'Ereignisse'}
                  </span>
                </div>
              )}
              {block.ereignisse.map((e, i) => {
                const index = (versatz[bi] ?? 0) + i
                return (
                  <div
                    key={e.id}
                    data-markiert={index === markiert}
                    className={cn(
                      'flex items-center gap-2.5 border-b border-line py-2 text-[12.5px] last:border-b-0',
                      index === markiert && 'bg-accent-soft/50',
                    )}
                  >
                    <span className="tabular w-[38px] shrink-0 text-[11.5px] text-muted">
                      {new Date(e.occurredAt).toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span className="shrink-0 rounded-md bg-accent-soft px-2 py-px text-[10.5px] font-bold text-accent-deep">
                      {beschriftung(e)}
                    </span>
                    <AufgabenVerweis
                      aufgabeId={e.taskId}
                      className="min-w-0 flex-1 cursor-pointer truncate text-left font-semibold hover:underline"
                      title={e.taskTitle}
                    >
                      {e.taskTitle}
                    </AufgabenVerweis>
                    {/* Im gruppierten Blick steht der Name schon in der Überschrift. */}
                    {block.person === null && auswahl.personId === null && e.personName && (
                      <span className="shrink-0 text-[11.5px] text-muted">{e.personName}</span>
                    )}
                    {/**
                     * **Beide Quellen an einer Zeile**, wenn beide denselben Vorgang gemeldet
                     * haben. Vorher standen dafür zwei Zeilen da – und der Balken im Leitstand
                     * zählte trotzdem eine, weil er `DISTINCT task_id` nimmt. Zwei Zahlen für
                     * denselben Tag, nebeneinander auf dem Bildschirm.
                     */}
                    <SourceBadge source={e.sourceId as TaskSource} />
                    {e.weitereQuellen?.map((q) => (
                      <SourceBadge key={q} source={q as TaskSource} />
                    ))}
                  </div>
                )
              })}
            </div>
          ))}

          <p className="mt-2.5 text-[11px] text-muted">
            Klick auf eine Aufgabe öffnet das Detail · ↑ ↓ blättern, Enter öffnet, Esc schließt ·
            Quelle: Ereignis-Log – automatisch, keine Zeiterfassung.
          </p>
        </div>
      </div>
    </div>
  )
}

type EreignisFuerGruppe = { type: TaskEventType; statusNach?: string | null }

/** Ereignisse ohne erkennbaren Urheber – aber sie gehören trotzdem in die Liste. */
const OHNE_PERSON = 'Ohne Person'

/**
 * Bündelt die Ereignisse eines Tages nach Person – die Reihenfolge fürs Daily.
 *
 * **Sortiert nach Menge, nicht nach Namen.** Wer viel gemeldet hat, steht oben; das
 * ist die Reihenfolge, in der man im Daily durchgeht. Bei Gleichstand entscheidet
 * der Name, damit die Liste zwischen zwei Tagen nicht ohne Grund springt.
 *
 * **Innerhalb einer Person bleibt die Zeit stehen** – die Abfrage liefert absteigend
 * sortiert, und das ist genau die Frage, die im Daily gestellt wird: was zuletzt lief.
 *
 * „Ohne Person" steht am Ende, egal wie groß der Block ist: Ereignisse ohne Urheber
 * sind nichts, womit ein Daily anfängt.
 */
export function nachPersonBuendeln<T extends { personName?: string | null }>(
  ereignisse: T[],
): { person: string; ereignisse: T[] }[] {
  const bloecke = new Map<string, T[]>()
  for (const e of ereignisse) {
    const person = e.personName ?? OHNE_PERSON
    const vorhanden = bloecke.get(person)
    if (vorhanden) vorhanden.push(e)
    else bloecke.set(person, [e])
  }

  return [...bloecke.entries()]
    .map(([person, ereignisse]) => ({ person, ereignisse }))
    .sort((a, b) => {
      if (a.person === OHNE_PERSON) return 1
      if (b.person === OHNE_PERSON) return -1
      return b.ereignisse.length - a.ereignisse.length || a.person.localeCompare(b.person, 'de-DE')
    })
}

/**
 * Beschriftung eines Ereignisses. Bei einem Statuswechsel steht das Ziel dran
 * („→ In Arbeit") – ohne es sagen alle Statuswechsel eines Tages dasselbe.
 */
function beschriftung(e: { type: TaskEventType; statusNach?: string | null }): string {
  if (e.type === 'status_changed' && e.statusNach) {
    const ziel = TASK_STATUS_LABELS[e.statusNach as TaskStatus] ?? e.statusNach
    return e.statusNach === 'erledigt' ? `✓ ${ziel}` : `→ ${ziel}`
  }
  return TASK_EVENT_TYPE_LABELS[e.type]
}
